import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    Resolve,
    RouterStateSnapshot,
} from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';
import {
    ClientsService,
    FinanceService,
    JobService,
    UserService,
} from './modules';

@Injectable({
    providedIn: 'root',
})
export class AppResolver implements Resolve<any> {
    constructor(
        private _clientsService: ClientsService,
        private _financeService: FinanceService,
        private _userService: UserService,
        private _jobService: JobService
    ) {}

    // The load currently in progress, if any. A navigation that's cancelled
    // and started again while this is still loading (e.g. clicking around
    // the menu while the app is starting up) used to throw it away and send
    // every request again from scratch — so the app could take far longer
    // to arrive, or not arrive at all while the clicks kept coming. Now a
    // retried navigation joins the load already under way. Only while it's
    // in flight: once finished, the next resolve loads fresh, as before.
    private _inFlight: Observable<any> | null = null;

    resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<any> {
        if (this._inFlight) return this._inFlight;

        // Fork join multiple API endpoint calls to wait all of them to finish
        const load = forkJoin([
            this._clientsService.getMyClients(),
            this._financeService.getCurrencies(),
            this._financeService.getFinanceStatuses(),
            this._userService.getUsers(),
            this._userService.getUsersWithFinances(),
            this._jobService.getJobTypes(),
            this._userService.loadUserAvatars(),
        ]).pipe(
            finalize(() => {
                if (this._inFlight === load) this._inFlight = null;
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this._inFlight = load;
        return load;
    }
}
